'use client';

import React from 'react';
import * as Sentry from 'utils/errorTracking';

// Components
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Button } from '@startengine/design-system.ui.button';
import { styled } from '@mui/material/styles';
import Image from 'next/legacy/image';

// Utils
import { imagePath } from 'utils/helpers';

// Styled Components following the established pattern from RenderError
const MainBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '50vh',
  padding: '40px 20px',
}));

const MainText = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '34px',
  lineHeight: '40px',
  textAlign: 'center',
  letterSpacing: '0.05px',
  color: theme.palette.secondary.main,
  marginBottom: '16px',
  [theme.breakpoints.down('md')]: {
    fontSize: '28px',
    lineHeight: '34px',
  },
}));

const SubText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '20px',
  lineHeight: '24px',
  textAlign: 'center',
  letterSpacing: '0.05px',
  color: theme.palette.common.tabsTextColor,
  marginBottom: '32px',
  maxWidth: '500px',
  [theme.breakpoints.down('md')]: {
    fontSize: '18px',
    lineHeight: '22px',
    marginBottom: '24px',
  },
}));

const ErrorDetails = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '18px',
  textAlign: 'center',
  color: theme.palette.grey[600],
  marginTop: '24px',
  padding: '16px',
  backgroundColor: theme.palette.grey[50],
  borderRadius: '8px',
  maxWidth: '600px',
  fontFamily: 'monospace',
  wordBreak: 'break-word',
}));

class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      errorDetails: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      errorDetails: {
        message: error?.message,
        stack: error?.stack,
        name: error?.name,
      },
    };
  }

  componentDidCatch(_error, errorInfo) {
    // Check if this is a webkit.messageHandlers related error
    const isWebKitError =
      _error?.message?.includes('webkit.messageHandlers') ||
      _error?.message?.includes('messageHandlers') ||
      (_error?.message?.includes('undefined is not an object') &&
        _error?.message?.includes('window.webkit'));

    // Check if this is a browser extension ethereum property descriptor error
    const isEthereumPropertyError =
      _error?.message?.includes('Invalid property descriptor') ||
      _error?.message?.includes(
        'Cannot both specify accessors and a value or writable attribute',
      ) ||
      (_error?.message?.includes('Cannot redefine property') &&
        _error?.message?.includes('ethereum')) ||
      (_error?.message?.includes('Cannot set property ethereum') &&
        _error?.message?.includes('which has only a getter'));

    // Check if error comes from a browser extension (chrome-extension:// URL in stack trace)
    const isExtensionError =
      _error?.stack?.includes('chrome-extension://') ||
      _error?.stack?.includes('moz-extension://') ||
      _error?.stack?.includes('safari-extension://') ||
      _error?.stack?.includes('ms-browser-extension://');

    // Check if this is a PostHog network error
    const isPostHogNetworkError =
      _error?.message?.includes('Failed to fetch') &&
      (_error?.message?.includes('posthog') ||
        _error?.message?.includes('us.i.posthog.com') ||
        _error?.message?.includes('us-assets.i.posthog.com'));

    // Don't send certain third-party/extension errors to Sentry
    if (!isWebKitError && !isEthereumPropertyError && !isExtensionError && !isPostHogNetworkError) {
      Sentry.captureException(_error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
        extra: {
          message: 'Critical app-level error caught by GlobalErrorBoundary',
          errorBoundary: 'GlobalErrorBoundary',
          level: 'critical',
        },
        tags: {
          errorBoundary: 'global',
          level: 'critical',
        },
      });
    } else if (isEthereumPropertyError || isExtensionError) {
      // Log suppressed extension errors for debugging
      // eslint-disable-next-line no-console
      console.warn(
        '[StartEngine] Suppressed browser extension error in GlobalErrorBoundary:',
        _error?.message,
      );
    }
  }

  handleReload = () => {
    if (typeof window !== 'undefined' && window.location) {
      window.location.reload();
    }
  };

  handleRetry = () => {
    this.setState({ hasError: false, errorDetails: null, showDetails: false });
  };

  toggleErrorDetails = () => {
    this.setState((prevState) => ({ showDetails: !prevState.showDetails }));
  };

  render() {
    const { hasError, errorDetails, showDetails } = this.state;
    const { children } = this.props;

    if (hasError) {
      return (
        <MainBox data-cy="global-error-boundary" data-testid="global-error-boundary">
          {/* Error Icon - using lightbulb icon as fallback if ErrorIcon doesn't exist */}
          <Box sx={{ mb: 3 }}>
            <Image
              src={`${imagePath}/images/icons/lightbulb-icon.svg`}
              alt="Error"
              height={120}
              width={120}
              style={{ opacity: 0.6 }}
            />
          </Box>

          <MainText>Something went wrong with the app</MainText>

          <SubText>
            We&apos;ve encountered a critical error and our team has been notified. Please refresh
            the page to continue using StartEngine.
          </SubText>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={this.handleReload}
              sx={{
                height: '48px',
                minWidth: '160px',
                backgroundColor: 'black',
                '&:hover': {
                  backgroundColor: '#333',
                },
              }}
              data-cy="reload-page-button"
              data-testid="reload-page-button"
            >
              Reload Page
            </Button>

            <Button
              variant="outlined"
              onClick={this.handleRetry}
              sx={{
                height: '48px',
                minWidth: '160px',
                borderColor: 'black',
                color: 'black',
                '&:hover': {
                  borderColor: '#333',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
              data-cy="try-again-button"
              data-testid="try-again-button"
            >
              Try Again
            </Button>
          </Box>

          {/* Error Details Toggle - Only show in development or if explicitly needed */}
          {process.env.NODE_ENV === 'development' && errorDetails && (
            <>
              <Button
                variant="text"
                onClick={this.toggleErrorDetails}
                sx={{
                  mt: 3,
                  fontSize: '14px',
                  color: 'grey.600',
                  textTransform: 'none',
                }}
                data-cy="toggle-error-details"
              >
                {showDetails ? 'Hide' : 'Show'} Error Details
              </Button>

              {showDetails && (
                <ErrorDetails>
                  <strong>Error:</strong> {errorDetails.message}
                  {errorDetails.stack && (
                    <>
                      <br />
                      <br />
                      <strong>Stack Trace:</strong>
                      <br />
                      {errorDetails.stack}
                    </>
                  )}
                </ErrorDetails>
              )}
            </>
          )}
        </MainBox>
      );
    }

    return children;
  }
}

export default GlobalErrorBoundary;
